import { Chromosome, Particle, QoSProperty, QoS_WEIGHTS, QoS_MAXIMIZE } from '../types/algorithm';

/**
 * Gets quality value from matrix
 * matrix[task][service][property]
 */
function getQualityValue(
  matrix: number[][][],
  task: number,
  service: number,
  property: number
): number {
  if (matrix[task] && matrix[task][service] && matrix[task][service][property] !== undefined) {
    return matrix[task][service][property];
  }
  return 0;
}

/**
 * Normalizes a value to [0, 1] range
 * For maximize: normalizes to [0, 1] where 1 is best
 * For minimize: normalizes to [1, 0] where 0 is best (inverted)
 */
function normalizeValue(
  value: number,
  min: number,
  max: number,
  maximize: boolean
): number {
  if (max === min) return 0.5; // Avoid division by zero

  if (maximize) {
    return (value - min) / (max - min);
  } else {
    // For minimization, invert: best (min) becomes 1, worst (max) becomes 0
    return (max - value) / (max - min);
  }
}

/**
 * Calculates fitness for a chromosome/particle solution
 * Fitness formula: 0.1 * (Σ(weight[i] * normalized_quality[i])) / 9
 */
export function calculateFitness(
  solution: number[], // Service selection array: solution[i] = service index for task i
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  // For Sequence workflow: aggregate properties across tasks
  const aggregatedValues: number[] = new Array(9).fill(0);

  // First pass: collect all values to find min/max for normalization
  const propertyRanges: Array<{ min: number; max: number }> = [];
  for (let prop = 0; prop < 9; prop++) {
    let min = Infinity;
    let max = -Infinity;

    for (let task = 0; task < taskCount; task++) {
      const serviceIdx = solution[task];
      if (serviceIdx >= 0 && serviceIdx < serviceCount) {
        const value = getQualityValue(matrix, task, serviceIdx, prop);
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    }

    propertyRanges.push({ min: min === Infinity ? 0 : min, max: max === -Infinity ? 0 : max });
  }

  // Second pass: aggregate normalized values
  // For Sequence: sum for all properties
  for (let prop = 0; prop < 9; prop++) {
    let sum = 0;
    const { min, max } = propertyRanges[prop];
    const maximize = QoS_MAXIMIZE[prop];

    for (let task = 0; task < taskCount; task++) {
      const serviceIdx = solution[task];
      if (serviceIdx >= 0 && serviceIdx < serviceCount) {
        const value = getQualityValue(matrix, task, serviceIdx, prop);
        const normalized = normalizeValue(value, min, max, maximize);
        sum += normalized;
      }
    }

    aggregatedValues[prop] = sum;
  }

  // Calculate weighted sum
  let weightedSum = 0;
  for (let prop = 0; prop < 9; prop++) {
    weightedSum += QoS_WEIGHTS[prop] * aggregatedValues[prop];
  }

  // Final fitness: 0.1 * weightedSum / 9
  const fitness = 0.1 * (weightedSum / 9);
  return fitness;
}

/**
 * Calculates fitness for chromosome
 */
export function calculateChromosomeFitness(
  chromosome: Chromosome,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  const fitness = calculateFitness(chromosome.x, matrix, taskCount, serviceCount);
  chromosome.fitness = fitness;
  return fitness;
}

/**
 * Calculates fitness for particle
 */
export function calculateParticleFitness(
  particle: Particle,
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  const fitness = calculateFitness(particle.position, matrix, taskCount, serviceCount);
  particle.fitness = fitness;
  
  // Update personal best
  if (fitness > particle.personalBestFitness) {
    particle.personalBest = [...particle.position];
    particle.personalBestFitness = fitness;
  }

  return fitness;
}

/**
 * Calculates aggregated response time (sum of response times)
 */
export function calculateResponseTime(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  let total = 0;
  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      total += getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime);
    }
  }
  return total;
}

/**
 * Calculates energy consumption (proxy calculation)
 * Energy = (alpha * RT + beta * Lat) * P
 * where alpha=0.6, beta=0.4, P=3.0W (cloud)
 */
export function calculateEnergy(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  const alpha = 0.6;
  const beta = 0.4;
  const P = 3.0;

  let totalEnergy = 0;
  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      const rt = getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime);
      const lat = getQualityValue(matrix, task, serviceIdx, QoSProperty.Latency);
      const energy = (alpha * rt + beta * lat) * P;
      totalEnergy += energy;
    }
  }
  return totalEnergy;
}

/**
 * Calculates execution cost (proxy calculation)
 * Cost = ExecutionTime * Coefficient (Coefficient=1.0)
 */
export function calculateCost(
  solution: number[],
  matrix: number[][][],
  taskCount: number,
  serviceCount: number
): number {
  const coefficient = 1.0;
  let totalCost = 0;

  for (let task = 0; task < taskCount; task++) {
    const serviceIdx = solution[task];
    if (serviceIdx >= 0 && serviceIdx < serviceCount) {
      const rt = getQualityValue(matrix, task, serviceIdx, QoSProperty.ResponseTime);
      totalCost += rt * coefficient;
    }
  }
  return totalCost;
}

